import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { api } from '@/lib/api';

interface DeskPage { key: string; label: string; section: string }
const errText = (e: any) => e?.response?.data?.message ?? 'Could not save.';

/**
 * The owner chooses what the desk officer can use. A switched-off page leaves the desk officer's menu,
 * and the server refuses their changes there (for staff and logins, it refuses showing them too).
 */
export type AccessRole = 'desk' | 'bursar' | 'teacher' | 'parent' | 'student' | 'applicant';
export function DeskAccessPage() { return <RoleAccessPage role="desk" />; }
export function BursarAccessPage() { return <RoleAccessPage role="bursar" />; }
export function TeacherAccessPage() { return <RoleAccessPage role="teacher" />; }
export function ParentAccessPage() { return <RoleAccessPage role="parent" />; }
export function StudentAccessPage() { return <RoleAccessPage role="student" />; }
export function ApplicantAccessPage() { return <RoleAccessPage role="applicant" />; }

export const ROLE_INFO: Record<AccessRole, { title: string; who: string; always: string; server: string }> = {
  desk: { title: 'Desk officer access', who: 'desk officer', always: 'Dashboard, notifications, their own clock-in and pay are always on.', server: 'the server refuses their changes there' },
  bursar: { title: 'Bursar access', who: 'bursar', always: 'Dashboard, notifications, their own clock-in and pay are always on.', server: 'the server refuses their changes there' },
  teacher: { title: 'Teacher access', who: 'teachers', always: 'Dashboard, clock-in, my pay, notifications and account are always on.', server: 'the server refuses their changes there' },
  parent: { title: 'Parent access', who: 'parents', always: 'Dashboard, my children, notifications and account are always on.', server: 'parents cannot open it at all' },
  student: { title: 'Student access', who: 'students', always: 'Dashboard, notifications and account are always on.', server: 'students cannot open it at all' },
  applicant: { title: 'Applicant access', who: 'applicants', always: 'Their application overview, notifications and account are always on.', server: 'applicants cannot use it' },
};

const naira = (n: number) => `₦${Number(n || 0).toLocaleString()}`;

/** How the bursar may move money: every payment waits for the owner, or the bursar pays with their own PIN up to a limit. */
function PaymentRule({ data, onSaved }: { data: any; onSaved: () => void }) {
  const [mode, setMode] = useState<'approval' | 'pin'>(data?.settings?.bursar_payments ?? 'approval');
  const [limit, setLimit] = useState<string>(data?.settings?.bursar_pay_limit != null ? String(data.settings.bursar_pay_limit) : '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const save = async () => {
    setBusy(true); setMsg(null);
    try { await api.put('/school/settings', { bursar_payments: mode, bursar_pay_limit: mode === 'pin' && limit ? Number(limit) : null }); setMsg({ ok: true, text: 'Saved.' }); onSaved(); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  };
  const opts = [
    ['approval', 'Every payment needs my approval', 'Transfers, expense payments, withdrawals and payroll the bursar starts wait for you. You approve with your PIN and the money goes.'],
    ['pin', 'The bursar pays with their own PIN', 'Payments go at once with the bursar\'s transaction PIN. Set a limit and anything bigger still waits for you.'],
  ] as const;
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 space-y-3">
      <div><h2 className="font-bold text-slate-900">Payments by the bursar</h2><p className="text-xs text-slate-500">Bank transfers, wallet transfers, expense payments, withdrawals and payroll approval.</p></div>
      <div className="grid sm:grid-cols-2 gap-2">
        {opts.map(([k, l, h]) => (
          <button key={k} onClick={() => setMode(k)} className={`text-left rounded-2xl border p-3.5 ${mode === k ? 'border-[#0A2540] ring-2 ring-[#0A2540]/10 bg-slate-50' : 'border-slate-200'}`}>
            <p className="text-sm font-bold text-slate-900">{l}</p><p className="text-xs text-slate-500 mt-0.5">{h}</p>
          </button>
        ))}
      </div>
      {mode === 'pin' && (
        <label className="block max-w-xs"><span className="text-sm font-semibold text-slate-700">Limit per payment (₦)</span>
          <input type="number" min={0} value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="No limit" className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm" />
          <span className="text-[11px] text-slate-500">{limit ? `Above ${naira(Number(limit))}, payments wait for you.` : 'Leave empty for no limit.'}</span></label>
      )}
      {msg && <p className={`text-sm rounded-xl px-3 py-2 ${msg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>{msg.text}</p>}
      <button disabled={busy} onClick={save} className="px-5 py-2.5 rounded-xl bg-[#0A2540] text-white text-sm font-bold disabled:opacity-40">{busy ? 'Saving…' : 'Save payment rule'}</button>
    </section>
  );
}

function RoleAccessPage({ role }: { role: AccessRole }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['school-settings'], queryFn: () => api.get('/school/settings').then((r) => r.data?.data) });
  const key = `${role}_hidden_pages`;
  const info = ROLE_INFO[role];
  const who = info.who;
  const pages: DeskPage[] = q.data?.role_pages?.[role] ?? q.data?.[`${role}_pages`] ?? [];
  const [off, setOff] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => { if (q.data) setOff(q.data.settings?.[key] ?? []); }, [q.data, key]);
  const saved: string[] = q.data?.settings?.[key] ?? [];
  const dirty = [...off].sort().join() !== [...saved].sort().join();
  const sections = useMemo(() => {
    const m = new Map<string, DeskPage[]>();
    pages.forEach((p) => { if (!m.has(p.section)) m.set(p.section, []); m.get(p.section)!.push(p); });
    return [...m.entries()];
  }, [pages]);

  const toggle = (k: string) => setOff((o) => (o.includes(k) ? o.filter((x) => x !== k) : [...o, k]));
  const save = async () => {
    setBusy(true); setMsg(null);
    try { await api.put('/school/settings', { [key]: off }); setMsg({ ok: true, text: `Saved. ${who[0].toUpperCase() + who.slice(1)} see the change next time their menu loads.` }); qc.invalidateQueries({ queryKey: ['school-settings'] }); qc.invalidateQueries({ queryKey: ['school-access'] }); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 sm:py-6 space-y-4 pb-28">
      <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">People & Access</p><h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">{info.title}</h1>
        <p className="text-sm text-slate-500">Choose what {role === 'desk' || role === 'bursar' ? `your ${who}` : `all ${who}`} can use. Switched-off pages disappear from their menu, and {info.server}.{['desk', 'bursar', 'teacher'].includes(role) ? ' Need a different set for some people? Make a custom role under Roles & permissions.' : ''}</p></div>

      {role === 'bursar' && q.data && <PaymentRule data={q.data} onSaved={() => qc.invalidateQueries({ queryKey: ['school-settings'] })} />}

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl border border-slate-200 p-4"><p className="text-xs text-slate-500">On</p><p className="text-2xl font-extrabold text-green-700">{pages.length - off.length}</p></div>
        <div className="bg-white rounded-2xl border border-slate-200 p-4"><p className="text-xs text-slate-500">Switched off</p><p className="text-2xl font-extrabold text-slate-900">{off.length}</p></div>
      </div>
      {msg && <p className={`text-sm rounded-xl px-3 py-2 ${msg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>{msg.text}</p>}

      {q.isLoading ? <p className="text-sm text-slate-500">Loading…</p> : sections.map(([sec, items]) => (
        <section key={sec} className="bg-white rounded-2xl border border-slate-200">
          <div className="flex items-center justify-between px-4 pt-3.5 pb-2">
            <h2 className="font-bold text-slate-900">{sec}</h2>
            <button onClick={() => { const keys = items.map((i) => i.key); const allOff = keys.every((k) => off.includes(k)); setOff((o) => allOff ? o.filter((x) => !keys.includes(x)) : [...new Set([...o, ...keys])]); }} className="text-xs font-semibold text-[#0A2540]">
              {items.every((i) => off.includes(i.key)) ? 'Turn all on' : 'Turn all off'}
            </button>
          </div>
          <ul className="divide-y divide-slate-100">
            {items.map((p) => {
              const on = !off.includes(p.key);
              return (
                <li key={p.key}>
                  <button onClick={() => toggle(p.key)} className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50">
                    {on ? <Eye className="w-4 h-4 text-green-600 shrink-0" /> : <EyeOff className="w-4 h-4 text-slate-400 shrink-0" />}
                    <span className={`flex-1 text-sm ${on ? 'text-slate-900 font-medium' : 'text-slate-400 line-through'}`}>{p.label}</span>
                    <span className={`relative w-11 h-6 rounded-full transition shrink-0 ${on ? 'bg-green-600' : 'bg-slate-300'}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? 'left-[22px]' : 'left-0.5'}`} /></span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 text-xs text-slate-600 flex gap-2"><ShieldCheck className="w-4 h-4 shrink-0 text-slate-400" />{info.always}</div>

      {dirty && (
        <div className="fixed bottom-0 inset-x-0 md:left-64 z-30 bg-white border-t border-slate-200 p-3 flex gap-2 justify-end">
          <button onClick={() => setOff(saved)} className="px-4 py-2.5 rounded-xl border border-slate-300 text-sm font-bold">Undo</button>
          <button disabled={busy} onClick={save} className="px-5 py-2.5 rounded-xl bg-[#0A2540] text-white text-sm font-bold disabled:opacity-40">{busy ? 'Saving…' : 'Save changes'}</button>
        </div>
      )}
    </div>
  );
}
